/**
 * The app's top-layer surfaces — today the HTML ordinal fullscreen viewer, a <dialog> shown with
 * `showModal()`.
 *
 * Everything outside an open modal dialog is inert, including elements promoted to the top layer
 * after it (measured: a `popover` shown above a modal dialog is still inert in Chromium and
 * WebKit), so anything that must stay usable while one is open — the global notice toasts — has
 * to render INSIDE it. Openers announce changes with `TOP_LAYER_CHANGE_EVENT` so such consumers
 * can re-home themselves, and the back-navigation policy closes the topmost one first.
 */

export const TOP_LAYER_CHANGE_EVENT = 'treechat:top-layer-change'

export function announceTopLayerChange(): void {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event(TOP_LAYER_CHANGE_EVENT))
}

/** The open modal <dialog> that currently owns the screen, if any. */
export function topmostModalDialog(): HTMLDialogElement | null {
    if (typeof document === 'undefined') return null
    const open = document.querySelectorAll<HTMLDialogElement>('dialog[open]')
    for (let index = open.length - 1; index >= 0; index -= 1) {
        const dialog = open[index]
        try {
            if (dialog.matches(':modal')) return dialog
        } catch {
            // `:modal` is Safari 15.6+; the app only ever opens dialogs modally.
            return dialog
        }
    }
    return null
}

/** Closes the topmost modal dialog. True when there was one to close. */
export function closeTopmostModalDialog(): boolean {
    const dialog = topmostModalDialog()
    if (!dialog) return false
    dialog.close()
    return true
}
